import { toast } from "sonner";
import { useCreateEvent, useUpdateEvent } from "@/api/hooks/use-events";
import { useCreateBuild } from "@/api/hooks/use-builds";
import type { EventId } from "@/types/identifiers";
import type { components } from "@/api/generated/schema";

type EventCreate = components["schemas"]["EventCreate"];
type EventUpdate = components["schemas"]["EventUpdate"];
type EventResponse = components["schemas"]["EventResponse"];

function buildsLabel(n: number): string {
  return `${n} build${n === 1 ? "" : "s"}`;
}

export interface AdminEventsActions {
  /**
   * Create the event, then fan out one build per boatId. Resolves with the new
   * event (so the page can auto-expand it); rejects if the event create itself
   * fails so the dialog stays open for retry. Build failures are reported but
   * do not reject — the event already exists.
   */
  handleCreateEvent: (data: EventCreate, boatIds: string[]) => Promise<EventResponse>;
  /** Resolves on success; rejects on failure so the dialog stays open. */
  handleUpdateEvent: (id: EventId, data: EventUpdate) => Promise<void>;
  /** Add builds to an existing event (e.g. late-entry teams). */
  handleGenerateBuilds: (eventId: EventId, boatIds: string[]) => Promise<void>;
  isSaving: boolean;
}

export function useAdminEventsActions(): AdminEventsActions {
  const createEvent = useCreateEvent();
  const updateEvent = useUpdateEvent();
  const createBuild = useCreateBuild();

  // Builds are independent — fan them out concurrently; a single boat's
  // failure (e.g. a 409 if it already has a build) doesn't sink the rest.
  async function fanOut(eventId: string, boatIds: string[]) {
    const results = await Promise.allSettled(
      boatIds.map((boat_id) =>
        createBuild.mutateAsync({ event_id: eventId, boat_id }),
      ),
    );
    const created = results.filter((r) => r.status === "fulfilled").length;
    return { created, failed: results.length - created };
  }

  return {
    handleCreateEvent: async (data, boatIds) => {
      let event: EventResponse;
      try {
        event = await createEvent.mutateAsync(data);
      } catch (err) {
        toast.error((err as Error).message);
        throw err;
      }
      if (boatIds.length === 0) {
        toast.success("Event created.");
        return event;
      }
      const { created, failed } = await fanOut(event.id, boatIds);
      if (failed > 0) {
        toast.warning(
          `Event created. ${buildsLabel(created)} added, ${failed} failed.`,
        );
      } else {
        toast.success(`Event created with ${buildsLabel(created)}.`);
      }
      return event;
    },

    handleUpdateEvent: (id, data) =>
      updateEvent.mutateAsync({ id, data }).then(
        () => {
          toast.success("Event updated.");
        },
        (err: Error) => {
          toast.error(err.message);
          throw err;
        },
      ),

    handleGenerateBuilds: async (eventId, boatIds) => {
      if (boatIds.length === 0) return;
      const { created, failed } = await fanOut(eventId, boatIds);
      if (failed > 0) {
        toast.warning(`${buildsLabel(created)} added, ${failed} failed.`);
      } else {
        toast.success(`${buildsLabel(created)} added.`);
      }
    },

    isSaving:
      createEvent.isPending || updateEvent.isPending || createBuild.isPending,
  };
}
